import Axios from "axios";
import { getPrefix, getSessionToken } from "../bandung";
import { isValidPassword, objectToFormData } from "./helpers";

const getUserPrefix = () => `${getPrefix()}/app/user`;

const getUserDeleteEndPoint = () => `${getUserPrefix()}/delete`;
const getUserListEndPoint = () => `${getUserPrefix()}/list`;
const getUserImageEndPoint = (
  userEntityId,
  userVersion) => `${getUserPrefix()}/imageimage?userEntityId=${userEntityId}&t=${userVersion}`;
const getUserTextLongDescriptionEndPoint = (userEntityId) => `${getUserPrefix()}/textlongdescription?userEntityId=${userEntityId}`;
const getUserTextShortDescriptionEndPoint = (userEntityId) => `${getUserPrefix()}/textshortdescription?userEntityId=${userEntityId}`;
const getUserUpdateEndPoint = () => `${getUserPrefix()}/update`;
const getUserUpdateLongDescriptionEndPoint = () => `${getUserPrefix()}/updatelongdescription`;

const getUserUpdatePasswordEndPoint = () => `${getUserPrefix()}/updatepassword`;
const getUserUpdateShortDescriptionEndPoint = () => `${getUserPrefix()}/updateshortdescription`;
const getUserUploadImageEndPoint = () => `${getUserPrefix()}/uploadimage`;
const getUserViewEndPoint = () => `${getUserPrefix()}/view`;

const userDelete = async (userEntityId) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
  });
  try {
    const response = await Axios.post(getUserDeleteEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "User deleted successfully",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userList = async (page) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    page: page,
  });
  try {
    const response = await Axios.post(getUserListEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "User list loaded successfully",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userTextLongDescription = async (userEntityId) => {
  try {
    const response = await Axios.get(
      getUserTextLongDescriptionEndPoint(userEntityId));
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Long description retrieved successfully.",
        data: decodeURIComponent(response.data),
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userTextShortDescription = async (userEntityId) => {
  try {
    const response = await Axios.get(
      getUserTextShortDescriptionEndPoint(userEntityId));
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Short description retrieved successfully.",
        data: decodeURIComponent(response.data),
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userUpdate = async ({
  userEntityId,
  address1,
  address2,
  administrator,
  city,
  facebookUserId,
  firstName,
  gmailUserId,
  lastName,
  linkedinUserId,
  phone,
  postal,
  reminders,
  showOrder,
  skypeUserId,
  state,
  timezone,
  title,
  url,
  useHtml,
}) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
    address1: address1,
    address2: address2,
    administrator: administrator,
    city: city,
    facebookUserId: facebookUserId,
    firstName: firstName,
    gmailUserId: gmailUserId,
    lastName: lastName,
    linkedinUserId: linkedinUserId,
    phone: phone,
    postal: postal,
    reminders: reminders,
    showOrder: showOrder,
    skypeUserId: skypeUserId,
    state: state,
    timezone: timezone,
    title: title,
    url: url,
    useHtml: useHtml,
  });
  try {
    const response = await Axios.post(getUserUpdateEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Update successful.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userUpdateLongDescription = async (userEntityId, longDescription) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
    longDescription: encodeURIComponent(longDescription),
  });
  try {
    const response = await Axios.post(
      getUserUpdateLongDescriptionEndPoint(),
      form);
    if (response.data.entity) {
      return {
        code: "SUCCESS",
        message: "Long description updated successfully.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userUpdatePassword = async ({
  userEntityId,
  newPassword,
  newPasswordRepeat,
}) => {
  if (newPassword !== newPasswordRepeat) {
    return {
      code: "PASSWORD_MISMATCH",
      message: "Passwords don't match.",
    };
  }

  if (!isValidPassword(newPassword)) {
    return {
      code: "INVALID_PASSWORD",
      message: "Password must be at least 8 characters long, contain at least 1 letter and 1 number, and can only contain the special characters !@#$%^&*.",
    };
  }

  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
    passwordNew: newPassword,
    passwordRepeat: newPasswordRepeat,
  });
  try {
    const response = await Axios.post(getUserUpdatePasswordEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "User password updated successfully",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userUpdateShortDescription = async (userEntityId, shortDescription) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
    shortDescription: encodeURIComponent(shortDescription),
  });
  try {
    const response = await Axios.post(
      getUserUpdateShortDescriptionEndPoint(),
      form);
    if (response.data.entity) {
      return {
        code: "SUCCESS",
        message: "Short description updated successfully.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userUploadImage = async (image, userEntityId, onUpload) => {
  const form = objectToFormData({
    "sessionToken": getSessionToken(),
    "userEntityId": userEntityId,
    "image": image,
  });

  try {
    const response = await Axios.post(
      getUserUploadImageEndPoint(),
      form, {
        onUploadProgress: (progressEvent) => onUpload(progressEvent),
      });
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "New profile image saved.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const userView = async (userEntityId) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
  });
  try {
    const response = await Axios.post(getUserViewEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "User loaded successfully",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

export {
  getUserImageEndPoint,
  userDelete,
  userList,
  userTextLongDescription,
  userTextShortDescription,
  userUpdate,
  userUpdateLongDescription,
  userUpdatePassword,
  userUpdateShortDescription,
  userUploadImage,
  userView,
};